import React, { useState } from "react";

import {
  HARVEST_LOSS_SAMPLE_AREA_M2,
  HARVEST_LOSS_SAMPLE_LENGTH_CM,
  HARVEST_LOSS_SAMPLE_WIDTH_CM,
} from "../FieldApp/harvestLossMetrics";

export const HARVEST_LOSS_SAMPLING_HELP_TITLE =
  "Como coletar amostras de perda de colheita";

const TABS = [
  { id: "summary", label: "Resumo" },
  { id: "ideal", label: "Cobertura ideal" },
  { id: "field", label: "Na prática" },
];

const sampleAreaLabel = HARVEST_LOSS_SAMPLE_AREA_M2.toLocaleString("pt-BR", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function HarvestLossSamplingHelpContent() {
  const [activeTab, setActiveTab] = useState("summary");

  return (
    <div className="harvest-loss-sampling-help">
      <div
        className="harvest-loss-sampling-help__tabs"
        role="tablist"
        aria-label="Orientações de coleta"
      >
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            className={
              activeTab === tab.id
                ? "harvest-loss-sampling-help__tab harvest-loss-sampling-help__tab--active"
                : "harvest-loss-sampling-help__tab"
            }
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "summary" && (
        <div role="tabpanel" className="harvest-loss-sampling-help__panel">
          <p>
            Cada amostra usa um quadrado de{" "}
            <strong>
              {HARVEST_LOSS_SAMPLE_WIDTH_CM}×{HARVEST_LOSS_SAMPLE_LENGTH_CM} cm
            </strong>{" "}
            ({sampleAreaLabel} m²). Conte <strong>todas</strong> as
            sementes/grãos dentro dessa área e registre no app.
          </p>
          <p>
            O sistema converte a contagem para <strong>kg/ha</strong> e{" "}
            <strong>sacas/ha</strong> com base no PMS informado na ordem de
            serviço.
          </p>
          <ul>
            <li>Faça repetições em pontos diferentes do talhão.</li>
            <li>
              Escolha locais que representem a perda média da faixa colhida,
              não apenas o ponto mais visível.
            </li>
            <li>
              Registre cada repetição separadamente — o app calcula a última
              amostra, a média das três últimas e a média total do dia.
            </li>
          </ul>
        </div>
      )}

      {activeTab === "ideal" && (
        <div role="tabpanel" className="harvest-loss-sampling-help__panel">
          <p>
            Para estimar a perda com <strong>máxima precisão</strong> em uma
            faixa de colheita, o ideal é cobrir toda a largura da plataforma com
            quadrados lado a lado, sem lacunas.
          </p>
          <p>
            <strong>Exemplo:</strong> plataforma com{" "}
            <strong>10 m de largura</strong> e quadrados de{" "}
            {HARVEST_LOSS_SAMPLE_WIDTH_CM} cm (0,50 m):
          </p>
          <ul>
            <li>
              10 m ÷ 0,50 m = <strong>20 quadrados</strong> em fila, cobrindo
              toda a extensão.
            </li>
            <li>
              20 × {sampleAreaLabel} m² = <strong>5,00 m²</strong> amostrados
              naquele ponto ao longo da plataforma.
            </li>
            <li>
              Some as sementes dos 20 quadrados e divida por{" "}
              <strong>20</strong> para obter a contagem média por amostra de{" "}
              {sampleAreaLabel} m² — equivalente a uma leitura representativa de
              toda a faixa naquele trecho.
            </li>
          </ul>
          <p>
            Esse procedimento elimina viés de pontos isolados e reflete a perda
            real distribuída na largura da máquina. Na prática de campo, raramente
            é possível fazer as 20 coletas a cada parada; por isso usa-se um
            número menor de repetições bem escolhidas (veja a aba{" "}
            <strong>Na prática</strong>).
          </p>
        </div>
      )}

      {activeTab === "field" && (
        <div role="tabpanel" className="harvest-loss-sampling-help__panel">
          <p>
            Como não dá para repetir 20 coletas a cada parada, o operador deve
            escolher <strong>pontos representativos</strong> ao longo do talhão
            e, dentro de cada parada, posicionar o quadrado onde a perda
            condiz com a média da faixa colhida — não só onde há mais grãos
            visíveis.
          </p>
          <p>
            <strong>Recomendações:</strong>
          </p>
          <ol>
            <li>
              Distribua as repetições ao longo do dia e do talhão (frente,
              meio e fundo, diferentes linhas ou trechos).
            </li>
            <li>
              Evite contar apenas em “manchas” óbvias de perda ou em áreas
              atípicas (solo exposto, cabeceira, reentrância).
            </li>
            <li>
              Quando possível, alterne o lado do quadrado dentro da faixa
              (esquerda, centro, direita da plataforma) para aproximar a
              cobertura ideal descrita na aba <strong>Cobertura ideal</strong>.
            </li>
            <li>
              Mantenha o quadrado sempre com{" "}
              {HARVEST_LOSS_SAMPLE_WIDTH_CM}×{HARVEST_LOSS_SAMPLE_LENGTH_CM}{" "}
              cm; não reduza nem aumente a área na hora de contar.
            </li>
            <li>
              Registre no app imediatamente, no GPS do ponto — isso permite
              cruzar a amostra com o rastro da máquina na plataforma.
            </li>
          </ol>
          <p>
            Quanto mais repetições bem distribuídas, mais confiável fica a
            média exibida na plataforma (última amostra, média das 3 últimas e
            média total).
          </p>
        </div>
      )}
    </div>
  );
}
